if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      // SV-
      if (this.classList.contains('pdp-redirect-panel')) {
        return;
      }

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      const mainForm = this.productSection.querySelector('.js-product-form-main');
      this.productForm = mainForm ? mainForm.closest('product-form') : null;
      this.abortController = new AbortController();

      this.syncStickyQuantity();
      this.syncStickyDisplay();

      this.bindEvents();
    }

    syncStickyQuantity() {
      this.stickyForm = this.querySelector('.js-product-form');
      const stickyQuantityInput = this.stickyForm?.querySelector('[name="quantity"]');
      if (!stickyQuantityInput) return;

      const mainQuantityInput = this.productForm?.querySelector('[name="quantity"]');
      const { signal } = this.abortController;

      const setStickyQuantity = (value) => {
        const qty = parseInt(value, 10);
        stickyQuantityInput.value = Number.isFinite(qty) && qty > 0 ? qty : 1;
      };

      if (mainQuantityInput) {
        setStickyQuantity(mainQuantityInput.value);
        mainQuantityInput.addEventListener('input', () => setStickyQuantity(mainQuantityInput.value), { signal });
        mainQuantityInput.addEventListener('change', () => setStickyQuantity(mainQuantityInput.value), { signal });
      }

      // Pack selector and other Alpine-driven flows update the input via
      // direct .value assignment, which doesn't fire input/change events.
      // Listen for the theme's custom event instead.
      document.addEventListener('quantity-changed', (event) => {
        setStickyQuantity(event.detail?.quantity ?? mainQuantityInput?.value);
      }, { signal });

      // Re-read the main quantity at submit time as a final safety net.
      this.stickyForm.addEventListener('submit', () => {
        if (mainQuantityInput) setStickyQuantity(mainQuantityInput.value);
      }, { capture: true, signal });
    }

    syncStickyDisplay() {
      const stickyPriceEl = this.querySelector('[data-sticky-atc-price]');
      const stickyPackEl = this.querySelector('[data-sticky-atc-pack]');
      const stickyPackCountEl = this.querySelector('[data-sticky-atc-pack-count]');
      if (!stickyPriceEl && !stickyPackEl) return;

      const packSelection = this.productInfo?.querySelector('.pack-selection');
      const mainPackPriceEl = this.productInfo?.querySelector('.product-info__add-button .pack-price');
      const mainQuantityInput = this.productForm?.querySelector('[name="quantity"]');
      const { signal } = this.abortController;

      const update = () => {
        if (stickyPriceEl && mainPackPriceEl?.textContent.trim()) {
          stickyPriceEl.textContent = mainPackPriceEl.textContent.trim();
        }
        if (stickyPackEl) {
          if (packSelection) {
            const qty = parseInt(mainQuantityInput?.value, 10);
            if (Number.isFinite(qty) && qty > 0) {
              if (stickyPackCountEl) stickyPackCountEl.textContent = qty;
              stickyPackEl.hidden = false;
            }
          } else {
            stickyPackEl.hidden = true;
          }
        }
      };

      update();
      document.addEventListener('quantity-changed', update, { signal });
      document.addEventListener('price-changed', update, { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    bindEvents() {
      let pending = false;
      const reconcile = () => {
        pending = false;
        this.refreshState();
        StickyAtcPanel.handleScroll();
      };
      const scheduleReconcile = () => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(reconcile);
      };

      window.addEventListener('scroll', scheduleReconcile, {
        signal: this.abortController.signal,
        passive: true,
      });

      // Initial reconcile, plus another after window load in case the
      // browser only restores scroll after all resources have finished.
      reconcile();
      if (document.readyState !== 'complete') {
        window.addEventListener('load', reconcile, {
          signal: this.abortController.signal,
          once: true,
        });
      }
    }

    refreshState() {
      if (!theme.mediaMatches.md && this.productForm) {
        const rect = this.productForm.getBoundingClientRect();
        this.classList.toggle('sticky-atc-panel--out', rect.bottom > 0);
      } else if (theme.mediaMatches.md && this.productInfo) {
        const rect = this.productInfo.getBoundingClientRect();
        const isIntersecting = rect.bottom > 0 && rect.top < window.innerHeight;
        this.classList.toggle('sticky-atc-panel--out', isIntersecting);
      }
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
